import React, { useState, PropsWithChildren, useEffect } from 'react';
import { AcmeIncReplaceProvider } from './AcmeIncReplaceProvider';
import { onAuthStateChanged, getAuth } from 'firebase/auth';
import { AppSidebar } from '../components/sidebar/AppSidebar';
import NavigationBar from './NavigationBar';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { TenancyContextProvider } from '@/contexts/TenancyContextProvider';
import { ThemeProvider } from '@/contexts/ThemeContext';
import ErrorBoundary from '@/components/ui/ErrorBoundary';
import { Toaster } from '@/components/ui/sonner';
import { UsersListProvider } from '@/contexts/UsersListProvider'
import { ControlsProvider } from '@/components/ui/ControlCombobox';
import { TenancyGuard } from '@/components/TenancyGuard';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTenancy } from '@/contexts/TenancyContext';
import { TourProvider } from '@/components/tour';
import { UserContextProvider, useUser } from '@/contexts/UserContextProvider';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { PaywallWrapper } from './PaywallWrapper';
import { ThemeSettings } from '@/components/ThemeSettings';
import { Button } from '@/components/ui/button';
import { OnboardingProvider } from '@/onboarding/OnboardingContext';
import { OnboardingPill } from '@/onboarding/OnboardingPill';
import { OnboardingHost } from '@/onboarding/OnboardingHost';
import { IntegrationHooksProvider } from '@/contexts/IntegrationHooksContext';
import { FloatingChatButton } from '@/components/FloatingChatButton';
import { getEnhancedPageTitle, clearEntityPageTitle } from '@/app/routes';
import { handlePostLogin } from '@/services/authDomainService';

interface LayoutProps {
  tenancyName?: string;
}

export function Layout({ children, tenancyName = 'Acme Inc' }: PropsWithChildren<LayoutProps>) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
      setAuthChecked(true);
    });

    return () => unsubscribe();
  }, []);

  // Update document title based on current route
  useEffect(() => {
    const updateTitle = () => {
      const pathname = window.location.pathname;
      const pageTitle = getEnhancedPageTitle(pathname);
      document.title = pageTitle;
    };

    updateTitle();

    // Listen for route changes
    const handleRouteChange = () => {
      // Clear entity title when navigating away from detail pages
      clearEntityPageTitle();
      updateTitle();
    };

    // Listen for popstate events (browser back/forward)
    window.addEventListener('popstate', handleRouteChange);
    
    // Listen for pushstate/replacestate (programmatic navigation)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    history.pushState = function(...args) {
      originalPushState.apply(history, args);
      handleRouteChange();
    };
    
    history.replaceState = function(...args) {
      originalReplaceState.apply(history, args);
      handleRouteChange();
    };

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, []);

  // Update document title based on current route
  useEffect(() => {
    const updateTitle = () => {
      const pathname = window.location.pathname;
      const pageTitle = getEnhancedPageTitle(pathname);
      document.title = pageTitle;
    };

    updateTitle();

    // Listen for route changes
    const handleRouteChange = () => {
      // Clear entity title when navigating away from detail pages
      clearEntityPageTitle();
      updateTitle();
    };

    // Listen for popstate events (browser back/forward)
    window.addEventListener('popstate', handleRouteChange);
    
    // Listen for pushstate/replacestate (programmatic navigation)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    history.pushState = function(...args) {
      originalPushState.apply(history, args);
      handleRouteChange();
    };
    
    history.replaceState = function(...args) {
      originalReplaceState.apply(history, args);
      handleRouteChange();
    };

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, []);

  // Move GatedContent outside to ensure consistent hook order
  const GatedContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthReady, isAuthenticated } = useAuth();
    // Now we can safely use useTenancy hook here since we're inside the TenancyContextProvider
    const { activeTenancy } = useTenancy();
    const location = useLocation();
    const navigate = useNavigate();

    const { user: userOption } = useUser();
    const user = Array.isArray(userOption) ? userOption[0] : userOption;
    
    // Check if user has pending approval status
    const [isPendingApproval, setIsPendingApproval] = useState(false);
    const [pendingApprovalChecked, setPendingApprovalChecked] = useState(false);
    
    useEffect(() => {
      const checkPendingApproval = async () => {
        if (isAuthReady && isAuthenticated && !pendingApprovalChecked) {
          // Always call postLogin to check actual status from backend
          // Don't rely on sessionStorage cache as approval status may have changed
          try {
            const auth = getAuth();
            const currentUser = auth.currentUser;
            if (currentUser) {
              const idToken = await currentUser.getIdToken();
              const postLoginResult = await handlePostLogin(idToken);
              console.log('[Layout] postLogin check result:', postLoginResult);
              
              if (postLoginResult.success && postLoginResult.pendingApproval) {
                // User is still pending approval
                sessionStorage.setItem('pendingApprovalInfo', JSON.stringify({
                  tenancyId: postLoginResult.tenancyId,
                  tenancyName: postLoginResult.tenancyName,
                }));
                setIsPendingApproval(true);
                navigate('/pending-approval');
              } else {
                // User is approved or no pending status - clear any cached pending info
                sessionStorage.removeItem('pendingApprovalInfo');
                setIsPendingApproval(false);
              }
            }
          } catch (error) {
            console.error('[Layout] Failed to check pending approval status:', error);
          }
          
          setPendingApprovalChecked(true);
        }
      };
      
      checkPendingApproval();
    }, [isAuthReady, isAuthenticated, pendingApprovalChecked, navigate]);
    
    // Check if we're on a special route that should have no padding
    const isSpecialRoute = location.pathname.includes('documentdetail') || 
                          location.pathname.includes('document-detail') ||
                          location.pathname.includes('detail');
    // Handle different states without early returns
    let content;
    const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
    useEffect(() => {
      if (isAuthReady && isAuthenticated) {
        const done = localStorage.getItem('theme-onboarding-done');
        const hasProfileTheme = !!(user?.theme && typeof user.theme === 'object' 
          && 'mode' in user.theme && 'primaryColor' in user.theme && 'accentColor' in user.theme);
        if (hasProfileTheme) {
          if (!done) localStorage.setItem('theme-onboarding-done', '1');
          setIsThemeModalOpen(false);
          return;
        }
        if (!done) setIsThemeModalOpen(true);
      }
    }, [isAuthReady, isAuthenticated, user]);

    if (window.location.pathname === '/essential-eight' || window.location.pathname === '/privacy-act') {
      const isEssentialEightRoute = window.location.pathname === '/essential-eight';
      const isPrivacyActRoute = window.location.pathname === '/privacy-act';

      content = (
        <SidebarProvider open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <PaywallWrapper isEssentialEightRoute={isEssentialEightRoute} isPrivacyActRoute={isPrivacyActRoute}>
            <div className="flex min-h-screen w-full">
              <>
                {/* @ts-ignore - AppSidebar doesn't expose currentRoute in its props type but we need to pass it */}
                <AppSidebar currentRoute={window.location.pathname} />
              </>
              <div className="flex flex-col flex-1">
                <NavigationBar tenancyName={tenancyName} />
                <main className={`flex-1 ${isSpecialRoute ? '' : 'p-4'} overflow-auto`}>
                  {children}
                </main>
              </div>
            </div>
          </PaywallWrapper>
          <Toaster theme="light" richColors position="bottom-left" closeButton />
          <FloatingChatButton />
        </SidebarProvider>
      );
    } else if (!isAuthReady) {
      content = <div>Loading authentication...</div>;
    } else if (!isAuthenticated) {
      content = <Navigate to="/login" />;
    } else {
      // Check if current tenant is MSP-only and not on tenancy management page
      const isMSPTenant = activeTenancy?.isMSPOnly;
      const isTenancyManagementPage = window.location.pathname === '/msp-view';
      const shouldHideSidebar = isMSPTenant && !isTenancyManagementPage;
      
      content = (
        <SidebarProvider open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <div className="flex h-screen w-full overflow-hidden">
            {!shouldHideSidebar && <AppSidebar />}
            <div className="flex flex-col flex-1 min-h-0">
              <NavigationBar tenancyName={tenancyName} />
              <main className={`flex-1 ${isSpecialRoute ? '' : 'p-4'} overflow-y-auto min-h-0`}>
                {children}
              </main>
            </div>
          </div>
          <Toaster theme="light" richColors position="bottom-left" closeButton />
          <FloatingChatButton />
        </SidebarProvider>
      );
    }

    return (
      <OnboardingProvider>
        <>
          {content}
          <OnboardingPill />
          <OnboardingHost />
          {isThemeModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-background/20 backdrop-blur-xs" onClick={() => { localStorage.setItem('theme-onboarding-done', '1'); setIsThemeModalOpen(false); }} />
            <div className="relative z-10 w-full max-w-lg max-h-[55vh] bg-card rounded-md overflow-hidden shadow border border-border/30 flex flex-col">
              <div className="flex justify-between items-start p-3 pb-2 border-b border-border/30 bg-gradient-to-b from-primary/5 to-transparent">
                <div>
                  <h2 className="text-xl font-medium text-foreground">Choose your Theme</h2>
                  <p className="mt-1 text-xs text-muted-foreground">Select mode and colors to personalise your experience</p>
                </div>
                <button onClick={() => { localStorage.setItem('theme-onboarding-done', '1'); setIsThemeModalOpen(false); }} className="text-muted-foreground hover:text-foreground transition-colors rounded-md p-1.5 hover:bg-foreground/5" aria-label="Close">
                  ✕
                </button>
              </div>
              <div className="flex-1 overflow-auto p-3">
                <div className="space-y-3">
                  <div className="border border-border/20 bg-background/40 rounded-md p-3">
                    <h3 className="text-sm font-medium mb-1">Make it yours</h3>
                    <p className="text-xs text-muted-foreground">Pick a theme that suits you. You can change this anytime in Settings.</p>
                  </div>
                  <ThemeSettings />
                </div>
              </div>
              <div className="border-t border-border/30 p-3 bg-gradient-to-t from-primary/5 to-transparent">
                <div className="flex flex-col sm:flex-row justify-end items-center gap-2">
                  <Button variant="outline" onClick={() => { localStorage.setItem('theme-onboarding-done', '1'); setIsThemeModalOpen(false); window.location.reload(); }} className="w-full sm:w-auto">
                    Skip for now
                  </Button>
                  <Button onClick={() => { localStorage.setItem('theme-onboarding-done', '1'); setIsThemeModalOpen(false); window.location.reload(); }} className="w-full sm:w-auto">
                    Save & Continue
                  </Button>
                </div>
              </div>
            </div>
          </div>
          )}
        </>
      </OnboardingProvider>
    );
  };

  if (window.location.pathname === '/essential-eight' || window.location.pathname === '/privacy-act') {
    return (
      <ErrorBoundary>

        <AuthProvider>
          <ThemeProvider>
            <UserContextProvider>
              <TenancyContextProvider>
                <GatedContent>{children}</GatedContent>
              </TenancyContextProvider>
            </UserContextProvider>
          </ThemeProvider>
        </AuthProvider>

      </ErrorBoundary>
    )
  }

  if (!authChecked) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return (

    <ErrorBoundary>
      <TourProvider>
        <UsersListProvider>
          <ThemeProvider>
            <AuthProvider>
              <UserContextProvider>
                <TenancyContextProvider>
                  <IntegrationHooksProvider>
                    <ControlsProvider>
                      <AcmeIncReplaceProvider>
                        {window.location.pathname === '/msp-view' ? (
                        <GatedContent>{children}</GatedContent>
                      ) : (
                        <TenancyGuard>
                            <GatedContent>{children}</GatedContent>
                          </TenancyGuard>
                        )}
                    </AcmeIncReplaceProvider>
                    </ControlsProvider>
                  </IntegrationHooksProvider>
                </TenancyContextProvider>
              </UserContextProvider>
            </AuthProvider>
          </ThemeProvider>
        </UsersListProvider>
      </TourProvider>
    </ErrorBoundary>
  );
}